<template>
  <DialogWrapper
    class="offer-review-dialog"
    cancelBtnLabelI18nKey="common.btn.cancel"
    saveBtnLabelI18nKey="common.btn.next"
    :show="show"
    :loading="loading"
    :title="$t(`${i18nPrefix}.title`)"
    :disabledSaveBtn="loading || !eligibleApplications.length"
    :closeOnClickModal="false"
    @update:show="handleUpdateShow"
    @save="handleSend"
  >
    <div class="offer-review">
      <div class="candidate-label">{{ $t(`${i18nPrefix}.candidates`) }}</div>
      <div class="candidate-count">{{ $t(`${i18nPrefix}.total`, { count: visibleApplications.length }) }}</div>
      <TableList
        class="offer-review-table"
        row-key="application_id"
        noHead
        noAction
        noPagination
        :listData="visibleApplications"
        :tableColumns="tableColumns"
        :rowClassName="rowClassName"
        @mouseover.native="handleRowMouseOver"
        @mouseleave.native="clearHoveredApplication"
        @scroll.native.capture="clearHoveredApplication"
      >
        <template #name="{ row }">
          <el-tooltip
            effect="light"
            placement="top-start"
            popper-class="light-tooltip-popper"
            manual
            :value="show && !loading && hoveredApplicationId === String(row.application_id)"
            :content="getExceptionTip(row)"
            :disabled="!row.has_offer && !hasMissingEmail(row)"
            :enterable="false"
          >
            <div class="offer-review-name" :data-application-id="row.application_id">
              <el-link type="primary" :underline="false" @click="openDetail(row)">{{ row.other_name || '-' }}</el-link>
              <div v-if="row.has_offer" class="existing-offer">
                {{ $t(`${i18nPrefix}.existingOffer`) }}<template v-if="row.offer_created_time"> · {{ row.offer_created_time | formatDateTime('YYYY/MM/DD') }}</template>
              </div>
            </div>
          </el-tooltip>
        </template>
        <template #email="{ row }">
          <div v-auto-tooltip class="offer-review-email">{{ hasMissingEmail(row) ? $t(`${i18nPrefix}.missingEmail`) : row.email }}</div>
        </template>
        <template #action="{ row }">
          <el-popconfirm
            :title="$t('common.message.confirmDelete')"
            @confirm="removeApplication(row)"
          >
            <template #reference>
              <el-button type="text" icon="el-icon-delete" />
            </template>
          </el-popconfirm>
        </template>
      </TableList>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'

export default {
  components: {
    DialogWrapper,
    TableList
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    applicationList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList.label.offerReview',
      removedIds: [],
      hoveredApplicationId: null
    }
  },
  computed: {
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.name`),
          dataKey: 'other_name',
          slotName: 'name',
          width: 248,
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.email`),
          dataKey: 'email',
          slotName: 'email',
          width: 200,
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.action`),
          dataKey: 'application_id',
          slotName: 'action',
          align: 'center',
          width: 162,
          showOverflowTooltip: false
        }
      ]
    },
    visibleApplications () {
      return _.filter(this.applicationList, item => !_.includes(this.removedIds, item.application_id))
    },
    eligibleApplications () {
      return _.filter(this.visibleApplications, item => item.has_offer === false && !item.is_revoked && !this.hasMissingEmail(item))
    }
  },
  watch: {
    applicationList () {
      this.removedIds = []
      this.clearHoveredApplication()
    },
    show () {
      this.clearHoveredApplication()
    }
  },
  methods: {
    handleUpdateShow (show) {
      this.$emit('update:show', show)
    },
    hasMissingEmail (row) {
      return !_.trim(row.email)
    },
    getExceptionTip (row) {
      return _.compact([
        row.has_offer && this.$t(`${this.i18nPrefix}.existingOfferTip`),
        this.hasMissingEmail(row) && this.$t(`${this.i18nPrefix}.missingEmailTip`)
      ]).join(' ')
    },
    rowClassName ({ row }) {
      return row.has_offer || this.hasMissingEmail(row) ? 'offer-review-existing-row' : ''
    },
    handleRowMouseOver (event) {
      const row = event.target.closest('tr.offer-review-existing-row')
      const name = row && row.querySelector('.offer-review-name')
      this.hoveredApplicationId = name ? name.dataset.applicationId : null
    },
    clearHoveredApplication () {
      this.hoveredApplicationId = null
    },
    removeApplication (row) {
      this.clearHoveredApplication()
      this.removedIds.push(row.application_id)
    },
    openDetail (row) {
      this.$emit('openDetail', {
        job: row.job,
        candidate: {
          id: row.candidate_id
        }
      })
    },
    async handleSend () {
      if (!this.loading && this.eligibleApplications.length) {
        const eligibleApplications = this.eligibleApplications
        this.handleUpdateShow(false)
        await this.$nextTick()
        this.$emit('send', eligibleApplications)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.offer-review-dialog.dialog-fade-leave-active {
  // 切到新标签页后退场帧可能暂停，关闭时立即隐藏弹框。
  display: none;
  animation: none;
}

.offer-review {
  .offer-review-table {
    max-height: max(135px, calc(90vh - 240px));
    overflow-y: auto;

    ::v-deep .el-table__body tr.offer-review-existing-row {
      &,
      > td {
        background-color: #FDF5F6;
      }
    }
  }

  .candidate-label {
    color: #303133;
    font-weight: 500;
    line-height: 20px;
  }

  .candidate-count {
    margin: 8px 0 12px;
    color: #909399;
    font-size: 12px;
    line-height: 14px;
  }

  .existing-offer {
    font-size: 10px;
    line-height: 12px;
    color: #d95f63;
  }

  .offer-review-email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
